import React, { useMemo } from 'react';
import {
  mergeClasses,
  Overflow,
  OverflowItem,
  Menu,
  MenuTrigger,
  Button,
  MenuPopover,
  MenuList,
  useOverflowMenu,
  useIsOverflowItemVisible,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import { useScopeFilterMenuStyles } from '../styles';
import { IScopeFilterProps, IScopeFilterMenuProps } from '../types';

const OverflowMenuItem = ({ id, children }: { id: string, children: React.JSX.Element }) => {
  const isVisible = useIsOverflowItemVisible(id);

  if (isVisible) {
    return null;
  }

  return children;
};

export interface IOverflowMenuProps {
  filterMenuItems: IScopeFilterMenuProps[];
}

const OverflowMenu: React.FC<IOverflowMenuProps> = ({ filterMenuItems }) => {
  const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          className="scope-filters-overflow-trigger"
          icon={<MoreHorizontal20Regular />}
          ref={ref}
          appearance="subtle"
        />
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {filterMenuItems.map((item, index) => (
            <OverflowMenuItem id={index.toString()}>
              {React.isValidElement<IScopeFilterMenuProps>(item) && React.cloneElement(item, { inPopover: true })}
            </OverflowMenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export const ScopeFiltersOverflow = ({ className, children, ...rest }: IScopeFilterProps) => {
  const styles = useScopeFilterMenuStyles();

  const filterMenuItems = useMemo(() => React.Children.map(children, (child, index) => {
    if (child && React.isValidElement(child)) {
      return (
        <OverflowItem
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          id={index.toString()}
        >
          <div>
            {React.cloneElement(child, { ...rest })}
          </div>
        </OverflowItem>
      );
    }
    return null;
  }), [children, rest]);

  return (
    <Overflow>
      <div className={mergeClasses('scope-filters-overflow', styles.filterContainer, className)}>
        {filterMenuItems}
        <OverflowMenu filterMenuItems={children} />
      </div>
    </Overflow>
  );
};

ScopeFiltersOverflow.defaultProps = {
  className: '',
};
